import { apiURL, privateFilePreviewURL } from '@yingbaba/api';
import type { Portfolio, PortfolioMedia } from '@yingbaba/types';
import { marketplaceCover } from './assets';

export type PortfolioScope = 'public' | 'workspace' | 'admin';

export function portfolioMediaURL(file: PortfolioMedia, scope: PortfolioScope = 'public') {
  const path = `/cases/${file.portfolio_id}/media/${file.id}`;
  return scope === 'workspace'
    ? privateFilePreviewURL(`/workspace${path}`, true)
    : apiURL(scope === 'admin' ? `/admin${path}` : path);
}

export function portfolioImages(item: Portfolio) {
  return (item.media || []).filter((file) => file.kind === 'image');
}

export function portfolioCover(item: Portfolio, scope: PortfolioScope = 'public') {
  const cover = item.media?.find((file) => file.kind === 'cover');
  if (cover) return portfolioMediaURL(cover, scope);
  const image = portfolioImages(item)[0];
  if (image && (!item.cover || item.cover.startsWith('/assets/')))
    return portfolioMediaURL(image, scope);
  return marketplaceCover('cases', item);
}

export function portfolioVideo(item: Portfolio, scope: PortfolioScope = 'public') {
  const uploaded = item.media?.find((file) => file.kind === 'video');
  if (uploaded)
    return {
      kind: 'video' as const,
      src: portfolioMediaURL(uploaded, scope),
      duration: uploaded.duration_seconds || 0,
    };
  try {
    const url = new URL(item.video_url);
    if (url.protocol !== 'https:' || url.username || url.password) return null;
    // A platform's watch-page URL is not a media stream. Keep it as a link.
    return {
      kind: /\.(?:mp4|m4v|webm)$/i.test(url.pathname) ? ('video' as const) : ('link' as const),
      src: url.href,
      duration: 0,
    };
  } catch {
    return null;
  }
}

export function portfolioMediaLabel(item: Portfolio) {
  const video = portfolioVideo(item);
  if (video) return video.kind === 'video' ? '视频作品' : '外链作品';
  const count = portfolioImages(item).length;
  return count ? `图集 · ${count} 张` : '图文案例';
}

export function videoDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const value = Math.floor(seconds);
  const minutes = Math.floor(value / 60);
  return `${minutes}:${String(value % 60).padStart(2, '0')}`;
}
